import type { FC, ReactNode } from 'react';
import { vegaConversationJsonRendering } from '@gonfalon/dogfood-flags/dogfood-flags';
import { msg } from '@gonfalon/messages/msg';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { Icon } from '@launchpad-ui/icons';

import {
  getMentionResourceUrl,
  getResourceTypeIconName,
  parseMessageMentions,
} from '../../../../internal/components/mentions/mentionUtils';
import { JsonPayload } from '../../../conversation/JsonPayload';
import { splitPromptSegments } from '../../../conversation/promptSegments';

import * as styles from '../../../mentions/mentions.css';

/**
 * Renders a message string that may contain inline mention markers and JSON blocks.
 * Mention markers like @[CPU spike alert](Alert:123) are rendered as styled inline tokens
 * (same appearance as the editor's MentionToken). A JSON block on its own lines, bare or fenced,
 * renders as a pretty-printed code block so machine-written prompts (factory caller context,
 * alert payloads) stay readable without markdown. Other text is rendered as-is with whitespace preserved.
 *
 * JSON block rendering is gated on `vega-conversation-json-rendering`; when the flag is off the
 * message renders as mention-annotated plain text, which is the behavior that shipped before it.
 */
export const InlineContextMessage: FC<{ message: string }> = ({ message }) => {
  const { projectKey } = useProjectId();
  const jsonRenderingEnabled = vegaConversationJsonRendering();

  if (!jsonRenderingEnabled) {
    return <MentionText text={message} projectKey={projectKey} />;
  }

  const segments = splitPromptSegments(message);

  if (segments.length === 1 && segments[0].kind === 'text') {
    return <MentionText text={message} projectKey={projectKey} />;
  }

  return (
    <>
      {segments.map((segment, i) =>
        segment.kind === 'code' ? (
          <JsonPayload key={i} raw={segment.value} />
        ) : (
          <MentionText key={i} text={segment.value} projectKey={projectKey} />
        ),
      )}
    </>
  );
};

const MentionText: FC<{ text: string; projectKey: string }> = ({ text, projectKey }) => {
  const segments = parseMessageMentions(text);

  if (segments.length === 0 || (segments.length === 1 && segments[0].type === 'text')) {
    return <>{text}</>;
  }

  const parts: ReactNode[] = segments.map((segment, i) => {
    if (segment.type === 'mention' && segment.mention) {
      const { label, resourceType, id } = segment.mention;
      const url = getMentionResourceUrl(projectKey, resourceType, id);

      return (
        <span key={i} className={styles.inlineToken}>
          <Icon name={getResourceTypeIconName(resourceType)} size="tiny" />
          {label}
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.inlineTokenLinkIcon}
              aria-label={msg('vega.conversation.openResourceLabel', { resourceType: resourceType.toLowerCase() })}
            >
              <Icon name="link-external" size="tiny" />
            </a>
          )}
        </span>
      );
    }
    return segment.value;
  });

  return <>{parts}</>;
};
